import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import './Cart.css';
import {
  updateQuantity,
  removeItem,
  setCheckoutData,
} from '../../../slices/CartSlice';
import { Image_URL } from '../../../config/config';
import { useCurrency } from '../../../contexts/CurrencyContext';
import { checkSalePageStatus } from '../../../Services/ProductServices';

const Cart = () => {
  const cartItems = useSelector((state) => state.cart.items);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { formatPrice, getCurrencySymbol, isSriLanka } = useCurrency();
  const [isSaleEnabled, setIsSaleEnabled] = useState(false);

  useEffect(() => {
    const checkSaleStatus = async () => {
      try {
        const status = await checkSalePageStatus();
        setIsSaleEnabled(status.isEnabled);
      } catch (error) {
        console.error('Error checking sale status:', error);
        setIsSaleEnabled(false);
      }
    };
    checkSaleStatus();
  }, []);

  const getItemPrice = (item) => {
    if (isSriLanka()) {
      return isSaleEnabled && item.OfferPrice && parseFloat(item.OfferPrice) > 0
        ? parseFloat(item.OfferPrice)
        : parseFloat(item.Price);
    } else {
      return isSaleEnabled && item.InternationalOfferPrice && parseFloat(item.InternationalOfferPrice) > 0
        ? parseFloat(item.InternationalOfferPrice)
        : parseFloat(item.InternationalPrice || item.Price);
    }
  };

  const handleUpdateQuantity = (id, color, size, newQuantity) => {
    if (newQuantity > 0) {
      dispatch(
        updateQuantity({
          ProductId: id,
          Color: color,
          Size: size,
          quantity: newQuantity,
        })
      );
    }
  };

  const handleRemoveItem = (id, color, size) => {
    dispatch(removeItem({ ProductId: id, Color: color, Size: size }));
  };

  const handleCheckout = () => {
    const checkoutItems = cartItems.map(item => ({
      ...item,
      currentPrice: getItemPrice(item)
    }));
    dispatch(setCheckoutData(checkoutItems));
    navigate('/checkout');
  };

  const totalAmount = cartItems.reduce(
    (total, item) => total + getItemPrice(item) * item.quantity,
    0
  );

  const currencySymbol = getCurrencySymbol();

  const getProductImage = (item) => {
    const productImage = item.Image && item.Image.length > 0 
      ? Image_URL + 'products/thumbnails/' + item.Image
      : 'https://jbadmin.alephaz.com/assets/img/placeholder-image.jpg';
    
    return productImage;
  };

  const CartItem = ({ item }) => {
    const itemPrice = getItemPrice(item);
    const displayPrice = `${currencySymbol} ${itemPrice.toFixed(2)}`;
    
    return (
      <React.Fragment>
        <div className="cart-item">
          <p className="product-title mobile-view">{item.Title}</p>
          <div className="cart-item-details">
            <div className="item-image-container">
              <Link to={`/product/${item.Slug}`} className="product-link">
                <img
                  src={getProductImage(item)}
                  className="main-image"
                  alt={item.Title}
                  onError={(e) => {
                    e.target.src = 'https://jbadmin.alephaz.com/assets/img/placeholder-image.jpg';
                  }}
                />
              </Link>
            </div>
            <div className="item-details-content">
              <p className="product-title desktop-view">{item.Title}</p>
              <p className="item-price-mobile">
                <span className="label">Price:</span>
                <span className="value">{formatPrice(itemPrice)}</span>
              </p>
              <p className="product-size-text">
                <span className="label">Size:</span>
                <span className="value">{item.Size}</span>
              </p>
              <p className="product-quantity-text">
                <span className="label">Color:</span>
                <span className="value">{item.Color}</span>
              </p>
              <button
                className="remove-button-mobile"
                onClick={() => handleRemoveItem(item.ProductId, item.Color, item.Size)}
              >
                <i className="fa-regular fa-trash-can"></i>
              </button>
            </div>
          </div>
          <div className="cart-item-right">
            <div className="price-and-remove">
              <p className="item-price">{displayPrice}</p>
              <button
                className="remove-button"
                onClick={() => handleRemoveItem(item.ProductId, item.Color, item.Size)}
              >
                <i className="fa-regular fa-trash-can"></i>
              </button>
            </div>
            <div className="quantity-control">
              <button
                onClick={() => handleUpdateQuantity(item.ProductId, item.Color, item.Size, item.quantity - 1)}
                disabled={item.quantity <= 1}
              >
                -
              </button>
              <span>{item.quantity.toString().padStart(2, '0')}</span>
              <button
                onClick={() => handleUpdateQuantity(item.ProductId, item.Color, item.Size, item.quantity + 1)}
              >
                +
              </button>
            </div>
          </div>
        </div>
        <hr className="cart-divider" />
      </React.Fragment>
    );
  };

  return (
    <div className="container">
      <div className="cart-container">
        <div className="cart-header">
          <h2>Cart</h2>
        </div>
        <hr className="cart-divider" />
        <div className="cart-heading-divider"></div>
        {cartItems.length === 0 ? (
          <p className="no-items-message">No items in cart</p>
        ) : (
          <div className="cart-content">
            <div className="cart-items-container">
              <div className="cart-items">
                {cartItems.map((item) => (
                  <CartItem 
                    key={`${item.ProductId}-${item.Color}-${item.Size}-${item.Slug}`} 
                    item={item} 
                  />
                ))}
              </div>
            </div>
            <div className="cart-summary-container">
              <div className="cart-summary">
                <p className="summary-title">
                  <strong>Total Amount</strong>
                </p>
                {cartItems.map((item) => {
                  const itemPrice = getItemPrice(item);
                  const itemTotal = itemPrice * item.quantity;
                  return (
                    <div
                      key={`${item.ProductId}-${item.Color}-${item.Size}`}
                      className="summary-item"
                    >
                      <span>{item.Title}</span>
                      <span>
                        {currencySymbol} {itemTotal.toFixed(2)}
                      </span>
                    </div>
                  );
                })}
                <hr />
                <div className="summary-total">
                  <span>Total</span>
                  <span>{currencySymbol} {totalAmount.toFixed(2)}</span>
                </div>
                <button className="checkout-button" onClick={handleCheckout}>
                  Checkout
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Cart;